import { Link, useNavigate } from 'react-router-dom';
import { TableAction, TableColumn, Concert, ConcertGenre } from '@/types';
import { Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import { useConcerts, useDeleteConcert, useToggleConcertStatus, useTranslation, useConfirmDialog } from '@/hooks';
import toast from 'react-hot-toast';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';

const Concerts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const deleteConcertMutation = useDeleteConcert();
  const toggleStatusMutation = useToggleConcertStatus();

  const concertColumns: TableColumn<Concert>[] = [
    {
      sortable: true,
      key: 'title',
      header: 'Título',
      accessor: (concert) => (
        <div className="font-medium text-gray-900">
          <Link
            className="link"
            to={`/content/concerts/${concert.id}/edit`}>
            {concert.title}
          </Link>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      sortable: true,
      key: 'year',
      header: 'Año',
      accessor: (concert) => (
        <div className="text-gray-700">
          {concert.year}
        </div>
      )
    },
    {
      key: 'genres',
      header: 'Géneros',
      accessor: (concert) => (
        <div className="text-gray-700">
          <ConcertGenresList genres={concert.genres} />
        </div>
      ),
      className: 'min-w-32'
    },
    {
      sortable: true,
      key: 'views',
      header: 'Visitas',
      accessor: (concert) => (
        <div className="text-gray-700">
          {concert.views?.toLocaleString() || 0}
        </div>
      )
    },
    {
      key: 'active',
      header: 'Activo',
      accessor: (concert) => (
        <Toggle
          checked={concert.active}
          onChange={(checked) => handleToggleStatus(concert.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    },
    {
      key: 'updatedDate',
      sortable: true,
      header: t.common.fields.updatedDate,
      accessor: (sport) => (
        <div className="text-gray-500">
          {sport.updatedDate ? new Date(sport.updatedDate).toLocaleDateString('es'):'N/D'}
        </div>
      ),
    },
  ];

  const concertActions: TableAction<Concert>[] = [
    {
      label: 'Ver',
      icon: Eye,
      onClick: (concert) => console.log('View concert:', concert.id),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Editar',
      icon: Edit,
      onClick: (concert) => navigate(`/content/concerts/${concert.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Activar',
      icon: Eye,
      onClick: (concert) => handleToggleStatus(concert.id, !concert.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (concert) => !concert.active
    },
    {
      label: 'Desactivar',
      icon: Eye,
      onClick: (concert) => handleToggleStatus(concert.id, !concert.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (concert) => concert.active
    },
    {
      label: 'Eliminar',
      icon: Trash2,
      onClick: (concert) => handleDeleteConcert(concert.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handleCreateConcert = () => {
    navigate('/content/concerts/create');
  };

  const handleDeleteConcert = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: '¿Estás seguro de que deseas eliminar este concierto?',
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteConcertMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      toast.success('Concert deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      toast.success('Concert status updated successfully');
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={concertColumns}
          actions={concertActions}
          useQueryHook={useConcerts}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Conciertos</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateConcert} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo concierto
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Conciertos</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateConcert} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateConcert}
          searchPlaceholder="Buscar conciertos..."
          title="Conciertos"
          itemsPerPage={10}
        />
        {dialog}
      </div>
    </div>
  );
};

const ConcertGenresList = ({ genres }: { genres: ConcertGenre[] }) => {
  return (
    <div className="flex flex-wrap gap-1">
      {genres.map((genre) => (
        <span
          key={genre}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-400 text-white"
        >
          {genre.toString().charAt(0).toUpperCase() + genre.toString().slice(1).toLowerCase()}
        </span>
      ))}
    </div>
  );
}

export default Concerts;
